import { authStateAtom } from '@/auth/authStateAtom';
import { clearLocalData } from '@/utils/clearLocalData';
import { appStore } from '@/utils/jotaiStore';
import { datadogRum } from '@datadog/browser-rum';

// Clears the local authentication state. Module-level (non-hook) so it can be
//  called from non-React code: the Apollo error link and the axios 401
//  interceptor.
//
// If you want to end a user’s session on the backend, you also have to send
//  a logout request to the backend — see useLogout() in authentication.ts.
export const clearAuthenticationState = () => {
    // Already logged out — make repeated calls a no-op so a burst of
    //  UNAUTHENTICATED responses (e.g. several polling queries failing in the
    //  same tick) only tears things down once.
    if (appStore.get(authStateAtom) === null) {
        return;
    }

    // End the authed Datadog session at a clean boundary so post-logout
    //  activity starts a fresh anonymous session instead of detaching the user
    //  mid-session. The session-replay recorder restarts automatically with
    //  the new session (we are not in manual-recording mode), which begins on
    //  the next user interaction.
    if (datadogRum.getInitConfiguration()) {
        datadogRum.stopSession();
        datadogRum.clearUser();
    }

    // Setting the authentication atom is the source of truth for whether a
    //  user is authenticated. This makes the router automatically redirect to
    //  the sign-in page if the user was on a page that required
    //  authentication, makes ApolloProvider stop the client (killing any
    //  polling queries), and makes any API queries that require authentication
    //  wait until authenticatedUser is set again. Because the atom is backed
    //  by localStorage, the write also propagates to other tabs via the
    //  `storage` event, logging them out too.
    appStore.set(authStateAtom, null);

    // Clear browser storage AND the React Query cache so no PHI/PII lingers after
    //  logout. (Clearing the cache while authed components are briefly still mounted
    //  can make one of their queries refetch against the now-dead session and 401 —
    //  the axios interceptor parks that as a never-resolving "zombie" query. That is
    //  tolerated here because useEnterAuthenticatedSession() clears the cache again
    //  on the next sign-in, before any authed page mounts, so the zombie can never
    //  reach a consumer.)
    clearLocalData();

    // Q: DO WE NEED TO CLEAR ANY COOKIES?
    //
    // A: Strictly speaking, no.
    //
    // We couldn't clear up the `sessionid` cookie even if we wanted to, since it’s HttpOnly.
    //  But it’s actually okay if the user has an invalid or expired session ID cookie;
    //  the server just ignores it.
    //
    // We could clear up the `csrftoken` cookie here, but it really doesn’t matter.
    //  The back-end handles it just fine if the CSRF token is invalid: it returns a 401 and
    //  the user gets prompted to sign in again, which sets the CSRF token correctly.
};
